import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getVariantChildrenDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import getColor from "@insite/mobius/utilities/getColor";
import GuestQuoteRequestForm from "@ucs/Components/GuestRequestForm/GuestQuoteRequestForm";
import ProductGetQuote from "@ucs/Components/ProductDetails/ProductGetQuote";
import UCSColors from "@ucs/Utils/UCSColors";
import * as React from "react";
import { useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";
import { Roles } from "@ucs/Helpers/Enums";

const enum fields {
    overrideButtonText = "overrideButtonText",
    redirectOnSubmit= "redirectOnSubmit",
}

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState, ownProps: HasProduct) => ({
    configurationSelection: state.pages.productDetails.configurationSelection,
    configurationCompleted: state.pages.productDetails.configurationCompleted,
    variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
    userRoles: state.context.session!.userRoles,
    isAuthenticated: state.context.session!.isAuthenticated,
    state,
});

export interface ProductDetailsAddToCartButtonStyles {
    button?: ButtonPresentationProps;
}

export const addToCartButtonStyles: ProductDetailsAddToCartButtonStyles = {
    button: {
        css: css`
            width: 100%;
            background-color: white;
            border: 2px solid ${getColor("primary")};
            color: ${UCSColors.gray03};
            &:hover {
                color: white;
            }
        `,
    },
};

const styles = addToCartButtonStyles;

const ProductDetailsGetQuoteButton = ({
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    userRoles,
    isAuthenticated,
    product,
    state,
    fields,
}: Props) => {
    const [showQuoteModal, setShowQuoteModal] = useState(false);
    const variantChildren = getVariantChildrenDataView(state, product.id).value!;
    const isParentProduct = variantChildren && variantChildren.length > 0;

    const link = useGetLink(fields.redirectOnSubmit || "");
    const redirectOnSubmit: LinkFieldValue = {
        type: "Url",
        value: link?.url || "",
    };
    
    return (
        <>
            {isAuthenticated &&
            (userRoles.toLowerCase().includes("vip") || userRoles.toLowerCase().includes("buyer") 
            || userRoles.toLowerCase().includes(Roles.Admin.toLowerCase())) ? (
                <ProductGetQuote
                    configurationSelection={configurationSelection}
                    configurationCompleted={configurationCompleted}
                    variantSelectionCompleted={variantSelectionCompleted}
                    extendedStyles={styles.button}
                    labelOverride={fields.overrideButtonText}
                    data-test-selector="productGetQuote"
                />
            ) : (
                !isAuthenticated &&
                (
                    <>
                        <Button disabled={isParentProduct} onClick={() => setShowQuoteModal(true)} {...styles.button}>
                            {fields.overrideButtonText}
                        </Button>
                        <GuestQuoteRequestForm
                            isModalOpen={showQuoteModal}
                            toggleModal={() => setShowQuoteModal(!showQuoteModal)}
                            redirectOnSubmit={redirectOnSubmit}
                        />
                    </>
                )
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps)(ProductDetailsGetQuoteButton)),
    definition: {
        displayName: "Get Quote Button",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.overrideButtonText,
                displayName: "Override Button Text",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "Get Quote",
            },
            {
                name: fields.redirectOnSubmit,
                displayName: "Confirmation Page Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Url",
                },
                fieldType: "General",
            }
        ],
    },
};

export default widgetModule;
